import type { PostHog } from 'posthog-js';
import { afterInteraction } from '../common/afterInteraction';

type PostHogQueueItem
  = | { type: 'identify'; args: [string] }
    | { type: 'property'; args: [Record<string, unknown>?] };

function generateDistinctId() {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return `ph-${globalThis.crypto.randomUUID()}`;
  }

  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);

  const randomPart = Array.from(
    bytes,
    byte => byte.toString(16).padStart(2, '0'),
  ).join('');

  return `ph-${randomPart}`;
}

export default defineNuxtPlugin({
  name: 'posthog',
  setup(nuxtApp) {
    const app = useNuxtApp();
    const config = useRuntimeConfig();
    const posthogConfig = (config.public as any).posthog || {};
    const publicKey = posthogConfig.publicKey || 'phc_LndVfEvknjGUtyhxbOgktF8tggjuvYhQAquzBbqErjt';
    const host = posthogConfig.host || 'https://posthog.appercut.eu';

    const queue: PostHogQueueItem[] = [];
    let realPosthog: PostHog | null = null;
    let distinctId = '';

    function resolveDistinctId() {
      if (distinctId) {
        return distinctId;
      }

      try {
        const uuidCookie = useCookie('user-uuid');
        distinctId = String(uuidCookie.value || '');
      } catch {
        distinctId = '';
      }

      if (!distinctId) {
        distinctId = generateDistinctId();
      }

      return distinctId;
    }

    async function sendCapture(
      eventName: string,
      payload?: Record<string, unknown>,
    ) {
      try {
        const resolvedDistinctId = resolveDistinctId();
        const requestBody = JSON.stringify({
          api_key: publicKey,
          event: eventName,
          distinct_id: resolvedDistinctId,
          properties: {
            distinct_id: resolvedDistinctId,
            $user_id: resolvedDistinctId,
            ...payload,
          },
          timestamp: new Date().toISOString(),
        });

        if (typeof navigator.sendBeacon === 'function') {
          const blob = new Blob([requestBody], { type: 'application/json' });

          if (navigator.sendBeacon(`${host}/capture/`, blob)) {
            return;
          }
        }

        await fetch(`${host}/capture/`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          keepalive: true,
          body: requestBody,
        });
      } catch (error: unknown) {
        app.$logger.note({
          category: 'posthog',
          message: 'capture fallback failed',
          level: 'warning',
          cause: error,
        });
      }
    }

    const posthogStub = {
      capture: (eventName: string, payload?: Record<string, unknown>) => {
        globalThis.dispatchEvent(new CustomEvent('__posthog-capture', {
          detail: {
            event: eventName,
            properties: payload,
          },
        }));

        if (realPosthog) {
          realPosthog.capture(eventName, payload);
          return;
        }

        void sendCapture(eventName, payload);
      },
      identify: (id: string) => {
        distinctId = id;

        if (realPosthog) {
          realPosthog.identify(id);
        } else {
          queue.push({ type: 'identify', args: [id] });
        }
      },
      setPersonProperties: (properties: Record<string, unknown>) => {
        if (realPosthog) {
          realPosthog.setPersonProperties(properties);
        } else {
          queue.push({ type: 'property', args: [properties] });
        }
      },
    };

    function replayQueue() {
      if (!realPosthog) return;

      for (const item of queue) {
        try {
          if (item.type === 'identify') {
            realPosthog.identify(item.args[0]);
          } else if (item.type === 'property') {
            realPosthog.setPersonProperties(item.args[0]);
          }
        } catch (error: unknown) {
          app.$logger.note({
            category: 'posthog',
            message: 'queued call failed',
            level: 'warning',
            cause: error,
          });
        }
      }

      queue.length = 0;
    }

    nuxtApp.hook('app:mounted', () => {
      afterInteraction().then(() => import('posthog-js')).then((mod) => {
        realPosthog = mod.default.init(publicKey, {
          api_host: host,
          capture_pageview: false,
          ...posthogConfig.clientConfig,
        });
        replayQueue();
      }).catch((error: unknown) => {
        app.$logger.note({
          category: 'posthog',
          message: 'sdk failed to load',
          level: 'warning',
          cause: error,
        });
      });
    });

    return {
      provide: {
        posthog: posthogStub,
      },
    };
  },
});
